import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  workflowCanvasShortcutAvailable,
  workflowModelFromContext,
  type WorkflowClipboardOps,
} from './workflow-toolbar-action.helpers';

/**
 * Undo the last graph edit (Cmd/Ctrl+Z).
 *
 * A framework action rather than a bare key listener so it shares the editor's
 * keybinding wiring, focus/modal gating and the command palette with copy, paste
 * and delete — and so the actual work stays in {@link WorkflowModel.undo}. No
 * toolbar button (like the clipboard actions); it reaches the user through the
 * shortcut and the palette.
 */
export class WorkflowUndoAction extends WorkflowEditorToolbarActionBase {
  title = 'Undo';
  override description = 'Undo the last change to the graph';
  group = 'Workflow';
  icon = 'undo';
  shortcut = ['ctrl', 'z'];

  constructor(private readonly ops: WorkflowClipboardOps) {
    super();
  }

  /** Leave Cmd+Z to a text field or a dialog stacked over the canvas. */
  shortcutAvailable(): boolean {
    return workflowCanvasShortcutAvailable(this.ops);
  }

  /** Only when there is an edit to undo — also hides it from the palette. */
  override applicable(context: ApplicableContext): boolean {
    const model = workflowModelFromContext(context);
    return model !== null && model.canUndoWatchable.snapshot();
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    workflowModelFromContext(context)?.undo();
    return ok({});
  }
}
